import { useState } from "react";
import { mockComments } from "@/mocks/community";

interface Post {
  id: string;
  author: string;
  avatar: string;
  niche: string;
  title: string;
  content: string;
  image: string;
  rating: number;
  ratingCount: number;
  comments: number;
  likes: number;
  tags: string[];
  timeAgo: string;
}

interface PostModalProps {
  post: Post;
  onClose: () => void;
}

export default function PostModal({ post, onClose }: PostModalProps) {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState(mockComments.filter((c) => c.postId === post.id));
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    const newComment = {
      id: `c${Date.now()}`,
      postId: post.id,
      author: "You",
      avatar: "https://public.readdy.ai/ai/img_res/0417795618b9307d68c68ee61abf8f31.jpg",
      content: comment,
      timeAgo: "Just now",
      likes: 0,
    };
    setComments((prev) => [newComment, ...prev]);
    setComment("");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="font-semibold text-gray-900 text-sm">Post Details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Image */}
        <div className="h-56 overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
        </div>

        <div className="p-6">
          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-3">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-medium">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {post.title}
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed mb-5">{post.content}</p>

          {/* Author */}
          <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-100">
            <img src={post.avatar} alt={post.author} className="w-10 h-10 rounded-full object-cover object-top" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">{post.author}</p>
              <p className="text-gray-400 text-xs">{post.niche} · {post.timeAgo}</p>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <i className="ri-star-fill text-amber-400 text-sm" />
              <span className="text-sm font-semibold text-gray-700">{post.rating}</span>
              <span className="text-gray-400 text-xs">({post.ratingCount})</span>
            </div>
          </div>

          {/* Rate This Post */}
          <div className="mb-5 pb-5 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-2">Rate this post:</p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setUserRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="cursor-pointer"
                >
                  <i
                    className={`text-xl ${
                      (hoverRating || userRating) >= star ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"
                    }`}
                  />
                </button>
              ))}
              {userRating > 0 && (
                <span className="text-xs text-teal-600 ml-2 font-medium">Thanks for rating!</span>
              )}
            </div>
          </div>

          {/* Comments */}
          <div>
            <h3 className="font-semibold text-gray-900 text-sm mb-4">
              Comments ({comments.length})
            </h3>

            {/* Add Comment */}
            <form onSubmit={handleSubmitComment} className="flex gap-3 mb-5">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                <i className="ri-user-line text-teal-600 text-sm" />
              </div>
              <div className="flex-1 flex gap-2">
                <input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Post
                </button>
              </div>
            </form>

            {/* Comment List */}
            <div className="space-y-4">
              {comments.length === 0 && (
                <p className="text-gray-400 text-sm text-center py-4">Be the first to comment!</p>
              )}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3">
                  <img src={c.avatar} alt={c.author} className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0" />
                  <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-900">{c.author}</span>
                      <span className="text-xs text-gray-400">{c.timeAgo}</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
